import React, { useState, useEffect } from "react";
import { X, UserCheck, Eye, RefreshCcw } from "lucide-react";
import { GetDeactivatedMatches } from "./apis/get";
import { PutReactivatePair } from "./apis/put";
import toast from "react-hot-toast";

interface DeactivatedMatchesModalProps {
  userId: string;
  onClose: () => void;
}

export default function DeactivatedMatchesModal({ userId, onClose }: DeactivatedMatchesModalProps) {
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState<any[]>([]);
  const [selectedMatch, setSelectedMatch] = useState<any | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const formatDateTime = (value: any) => {
    if (!value) return "N/A";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
  };

  const getSummary = (match: any) => {
    const mp = match?.mp || {};
    const mmp = match?.mmp || {};
    return [
      `MP Score: ${mp.match_score ?? "N/A"}`,
      `MMP Score: ${mmp.llm_score ?? "N/A"}`,
      `Updated: ${formatDateTime(match?.latest_updated_at || mp.updated_at || mmp.updated_at)}`,
    ];
  };

  useEffect(() => {
    fetchMatches();
  }, [userId]);

  const fetchMatches = async () => {
    setLoading(true);
    try {
      const response = await GetDeactivatedMatches(userId);
      if (response && response.status === true) {
        setMatches(response.data || []);
      } else {
        toast.error(response?.message || "Failed to fetch deactivated matches");
      }
    } catch (error) {
      console.error("Error fetching deactivated matches:", error);
      toast.error("An error occurred while fetching deactivated matches.");
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!selectedMatch) return;
    setReactivating(true);
    try {
      const candidateId = selectedMatch.candidate_id;
      const response = await PutReactivatePair(userId, candidateId);
      if (response && response.status === "success") {
        toast.success("Match reactivated successfully");
        setShowConfirm(false);
        setSelectedMatch(null);
        fetchMatches(); // refresh list
      } else {
        toast.error(response?.detail || response?.message || "Failed to reactivate match");
      }
    } catch (error: any) {
      console.error("Error reactivating match:", error);
      toast.error(error.response?.data?.detail || "An error occurred");
    } finally {
      setReactivating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-slate-900 border border-slate-700 w-full max-w-5xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <UserCheck className="text-purple-400" />
            Deactivated Users for {userId}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/5 rounded-full text-slate-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
          ) : matches.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              No deactivated matches found.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {matches.map((match, idx) => {
                const candidate = match.candidate_profile || {};
                const user = match.user_profile || {};
                const mp = match.mp || {};
                const mmp = match.mmp || {};
                return (
                  <div key={idx} className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 flex flex-col gap-3 hover:bg-slate-800 transition-colors">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-bold text-white text-lg">{candidate.name || "Unknown Candidate"}</h3>
                        <p className="text-xs text-slate-400">Candidate ID: {match.candidate_id || "N/A"}</p>
                        <p className="text-xs text-slate-500">User: {user.name || "Unknown"} | {match.user_id || "N/A"}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider ${candidate.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                          {candidate.is_active ? "Active" : "Inactive"}
                        </span>
                        <div className="flex gap-1 flex-wrap justify-end">
                          {match.belongs_to?.map((bt: string) => (
                            <span key={bt} className="text-[10px] bg-slate-700 px-1.5 py-0.5 rounded text-slate-300 uppercase">
                              {bt}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedMatch(match)}
                      className="mt-auto w-full py-2 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                    >
                      <Eye size={16} /> Full Detail
                    </button>
                    <div className="grid grid-cols-1 gap-1 text-[10px] text-slate-400">
                      {getSummary(match).map((item) => (
                        <div key={item} className="rounded-lg bg-slate-950/40 border border-slate-800 px-2 py-1">
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Selected Match Details Modal */}
      {selectedMatch && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in zoom-in-95 duration-200">
          <div className="bg-slate-900 border border-slate-700 w-full max-w-3xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b border-slate-800">
              <h2 className="text-xl font-bold text-white">Deactivated User Details</h2>
              <button
                onClick={() => setSelectedMatch(null)}
                className="p-2 hover:bg-white/5 rounded-full text-slate-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-4 space-y-3">
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Candidate</p>
                    <p className="text-white text-lg font-bold">{selectedMatch.candidate_profile?.name || "Unknown"}</p>
                    <p className="text-slate-400 text-sm">ID: {selectedMatch.candidate_id || "N/A"}</p>
                    <p className="text-slate-500 text-xs">Gender: {selectedMatch.candidate_profile?.gender || "N/A"}</p>
                    <p className="text-slate-500 text-xs">
                      Location: {selectedMatch.candidate_profile?.city ? `${selectedMatch.candidate_profile.city}, ` : ""}
                      {selectedMatch.candidate_profile?.state || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Candidate Active</p>
                    <p className={`text-sm font-bold ${selectedMatch.candidate_profile?.is_active ? "text-emerald-400" : "text-rose-400"}`}>
                      {selectedMatch.candidate_profile?.is_active ? "Active" : "Inactive"}
                    </p>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-4 space-y-3">
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">User</p>
                    <p className="text-white text-lg font-bold">{selectedMatch.user_profile?.name || "Unknown"}</p>
                    <p className="text-slate-400 text-sm">ID: {selectedMatch.user_id || "N/A"}</p>
                    <p className="text-slate-500 text-xs">Location: {selectedMatch.user_profile?.city ? `${selectedMatch.user_profile.city}, ` : ""}{selectedMatch.user_profile?.state || "N/A"}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg bg-slate-900/60 border border-slate-800 p-3">
                      <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">MP Score</p>
                      <p className="text-white text-lg font-bold">{selectedMatch.mp?.match_score ?? "N/A"}</p>
                    </div>
                    <div className="rounded-lg bg-slate-900/60 border border-slate-800 p-3">
                      <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">MMP Score</p>
                      <p className="text-white text-lg font-bold">{selectedMatch.mmp?.llm_score ?? "N/A"}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-4 mb-6">
                <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-2">Match Status</p>
                <div className="flex flex-wrap gap-2 text-[10px]">
                  <span className="px-2 py-1 rounded bg-slate-800 text-slate-300">Belongs to: {(selectedMatch.belongs_to || []).join(", ") || "N/A"}</span>
                  <span className="px-2 py-1 rounded bg-slate-800 text-slate-300">MP: {selectedMatch.mp?.is_active ? "Active" : "Inactive"}</span>
                  <span className="px-2 py-1 rounded bg-slate-800 text-slate-300">MMP: {selectedMatch.mmp?.is_active ? "Active" : "Inactive"}</span>
                  <span className="px-2 py-1 rounded bg-slate-800 text-slate-300">Updated: {formatDateTime(selectedMatch.latest_updated_at)}</span>
                </div>
              </div>

              {selectedMatch.mmp?.reason && (
                <div className="mb-6">
                  <p className="text-xs text-slate-400 uppercase tracking-widest font-bold mb-1">Reason</p>
                  <p className="text-sm text-slate-300 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                    {selectedMatch.mmp.reason}
                  </p>
                </div>
              )}

              <div className="flex items-center gap-4 pt-4 border-t border-slate-800">
                <button
                  onClick={() => window.open(`/user-details/${selectedMatch.candidate_id}`, "_blank")}
                  className="flex-1 py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2 border border-slate-600"
                >
                  <Eye size={18} /> View Profile
                </button>
                <button
                  onClick={() => setShowConfirm(true)}
                  className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2 border border-emerald-500/50 shadow-[0_0_15px_rgba(16,185,129,0.3)]"
                >
                  <RefreshCcw size={18} /> Reactivate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-[100001] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-in fade-in zoom-in-95 duration-200">
          <div className="bg-slate-900 border border-slate-700 w-full max-w-md rounded-2xl shadow-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-2">Confirm Reactivation</h3>
            <p className="text-slate-400 mb-6">
              Are you sure you want to reactivate the match between <strong>{userId}</strong> and <strong>{selectedMatch?.candidate_id}</strong>?
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={reactivating}
                className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-lg transition-colors border border-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleReactivate}
                disabled={reactivating}
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {reactivating ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>Continue</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
